// SPDX-License-Identifier: BUSL-1.1

/**
 * Recent flag changes widget — compact activity list for the dashboard.
 *
 * Fetches the last 5 flag-related audit entries and renders them
 * in a compact card format with action badges and timestamps.
 *
 */

import type * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/lib/api-client';
import { relativeTime } from '@/lib/format';
import type { AuditResponse } from '@/types/api';
import { ACTION_COLORS, actionSuffix } from '@/types/api';

/** Badge color for a flag audit action. */
function actionBadgeClass(action: string): string {
  const suffix = actionSuffix(action);
  const color = ACTION_COLORS[suffix] ?? 'text-neutral-400';
  return color;
}

/** Human-readable label for a flag action. */
function actionLabel(action: string): string {
  const suffix = actionSuffix(action);
  const labels: Record<string, string> = {
    toggle: 'Toggled',
    create: 'Created',
    kill_switch: 'Kill Switch',
    promote: 'Promoted',
    rollback: 'Rolled Back',
  };
  return labels[suffix] ?? suffix;
}

export function FlagRecentChanges(): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['audit', 'flags-recent'],
    queryFn: () => apiFetch<AuditResponse>('/audit?resourceType=flag&limit=5'),
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Recent Flag Changes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  const entries = data?.entries ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Recent Flag Changes</CardTitle>
        <Link
          to="/audit"
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          Full log →
        </Link>
      </CardHeader>
      <CardContent className="p-0">
        {entries.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-neutral-500">
            No recent flag changes.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-800">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center gap-3 px-4 py-2.5 transition hover:bg-neutral-900/50"
              >
                <span
                  className={`shrink-0 text-xs font-medium ${actionBadgeClass(entry.action)}`}
                >
                  {actionLabel(entry.action)}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-neutral-300">
                  {entry.resourceId}
                </span>
                <time
                  className="shrink-0 font-mono text-[11px] text-neutral-500"
                  dateTime={entry.createdAt}
                  title={new Date(entry.createdAt).toLocaleString()}
                >
                  {relativeTime(entry.createdAt)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
